import React, {useState, useRef} from "react";
import axios from "../axios/axios";
import {Box, Text, Button, Stack, FormLabel, FormControl, Input, Select, Spinner, Switch, HStack} from "@chakra-ui/react";
import {Header} from "../components/Header";

export default function AdminClientsContainer() {
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [users, setUsers] = useState([]);
    const emailRef = useRef();
    const passwordRef = useRef();
    const nameRef = useRef();
    const roleRef = useRef();

    function fetchUsers() {
        axios
            .get("/admin/users/all")
            .then(response => setUsers(response.data || []))
            .catch(error => console.error("Error fetching all users:", error));
    }

    function formatDuration(ms) {
        const totalSeconds = Math.max(0, Math.round(ms / 1000));
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        if (minutes === 0) {
            return `${seconds}s`;
        }

        return `${minutes}min ${String(seconds).padStart(2, "0")}s`;
    }

    function buildSyncMessage(responseData, clientDurationMs) {
        const serverDuration = responseData?.duration_ms;
        const tasks = responseData?.tasks || {};
        const sections = responseData?.sections || {};

        return [
            "Synchronisation des clients et des projets effectuee avec succes.",
            `Duree totale : ${formatDuration(clientDurationMs)}`,
            serverDuration !== undefined ? `Traitement serveur : ${formatDuration(serverDuration)}` : null,
            `Clients/projets Asana synchronises : ${responseData?.projects ?? 0}`,
            `Projets locaux reutilises : ${responseData?.fallback_projects_used ?? 0}`,
            `Portefeuilles Asana inaccessibles : ${responseData?.inaccessible_portfolios ?? 0}`,
            `Sections synchronisees : ${sections.sections_synced ?? 0}`,
            `Projets ignores : ${(sections.projects_skipped ?? 0) + (tasks.projects_skipped ?? 0)}`,
            `Sections ignorees : ${tasks.sections_skipped ?? 0}`,
            `Sections supprimees/nettoyees : ${(sections.sections_removed ?? 0) + (tasks.sections_empty_removed ?? 0)}`,
            `Sections parcourues : ${tasks.sections ?? 0}`,
            `Taches parcourues : ${tasks.tasks ?? 0}`,
            `Taches ignorees : ${tasks.tasks_skipped ?? 0}`,
            `Taches avec temps devise : ${tasks.tasks_with_estimated ?? 0}`,
            `Taches avec temps passe : ${tasks.tasks_with_actual ?? 0}`,
        ].filter(Boolean).join("\n");
    }

    function handleRegisterWebhooks() {
        setLoading(true);
        setMessage("");
        setError(false);

        axios
            .post("/asana/register-webhooks")
            .then(() => {
                setLoading(false);
                setMessage("Webhooks Asana enregistres avec succes");
            })
            .catch(error => {
                setLoading(false);
                setError(true);
                console.error(error);
                setMessage("Erreur lors de l'enregistrement des webhooks");
            });
    }

    function handleClickToSyncClientAndProject() {
        const startedAt = performance.now();
        setLoading(true);
        setMessage("");
        setError(false);

        axios
            .get("/asana/sync-clients")
            .then(response => {
                setMessage(buildSyncMessage(response.data, performance.now() - startedAt));
                setLoading(false);
            })
            .catch(error => {
                setError(true);
                setLoading(false);
                console.error("Error fetching clients:", error);
                const details =
                    error?.response?.data?.details ||
                    error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    error?.message;

                setMessage([
                    "Erreur lors de la synchronisation des clients et des projets",
                    `Duree avant erreur : ${formatDuration(performance.now() - startedAt)}`,
                    details ? `Detail : ${details}` : null
                ].filter(Boolean).join("\n"));
            });
    }

    function handleClickToSyncUsers() {
        setLoading(true);
        setMessage("");
        setError(false);

        axios
            .get("/asana/sync-users")
            .then(response => {
                setLoading(false);
                fetchUsers();
                setMessage("Synchronisation des collaborateurs effectuee avec succes");
            })
            .catch(error => {
                setError(true);
                setLoading(false);
                console.error("Error fetching users:", error);
                setMessage("Erreur lors de la synchronisation des collaborateurs");
            });
    }

    function handleAddUser(e) {
        e.preventDefault();
        const email = emailRef.current.value;
        const password = passwordRef.current.value;
        const name = nameRef.current.value;
        const role = roleRef.current.value;

        axios
            .post("/addUser", {email, password, name, role})
            .then(response => {
                fetchUsers();
                setMessage("Collaborateur ajoute avec succes");
            })
            .catch(error => {
                console.error("Error adding user:", error);
                setMessage("Erreur lors de l'ajout du collaborateur");
            });
    }

    function handleToggleUser(user) {
        setLoading(true);
        setMessage("");
        setError(false);

        axios
            .patch(`/admin/users/${user.id}/active`, {is_active: !user.is_active})
            .then(() => {
                fetchUsers();
                setLoading(false);
                setMessage(`Collaborateur ${!user.is_active ? "active" : "masque"} avec succes`);
            })
            .catch(error => {
                setError(true);
                setLoading(false);
                console.error("Error updating user:", error);
                setMessage("Erreur lors de la mise a jour du collaborateur");
            });
    }

    React.useEffect(() => {
        fetchUsers();
    }, []);

    return (
        <Box width={"full"} px={{base: 2, md: 4}} py={{base: 3, md: 5}}>
            <Box>{loading && <Spinner size="sm" mt={2}/>}</Box>

            <Text color={error ? "red" : "green"} mt={2} fontSize={{base: "sm", md: "md"}} whiteSpace="pre-line">
                {message}
            </Text>

            <Stack spacing={6} width={{base: "full", md: "1/3"}}>
                <Header fontSize={{base: "lg", md: "xl"}}>Synchronisation Asana</Header>

                <Stack direction={{base: "column", md: "row"}} spacing={4}>
                    <Button colorScheme="blue" variant="outline" size={{base: "sm", md: "md"}}
                            onClick={handleRegisterWebhooks} isLoading={loading}>
                        Synchroniser les webhooks
                    </Button>

                    <Button colorScheme="blue" variant="outline" size={{base: "sm", md: "md"}}
                            onClick={handleClickToSyncClientAndProject} isLoading={loading}>
                        Synchroniser les clients et projets
                    </Button>

                    <Button colorScheme="blue" variant="outline" size={{base: "sm", md: "md"}}
                            onClick={handleClickToSyncUsers} isLoading={loading}>
                        Synchroniser les collaborateurs
                    </Button>
                </Stack>

                <Header fontSize={{base: "lg", md: "xl"}}>Ajouter un collaborateur</Header>

                <Stack as={"form"} direction={"column"} width={"full"} onSubmit={handleAddUser}
                       spacing={{base: 3, md: 4}}>
                    <FormControl>
                        <FormLabel htmlFor="email" fontSize={{base: "sm", md: "md"}}>
                            Email :
                        </FormLabel>
                        <Input type="email" id="email" name="email" ref={emailRef} size={{base: "sm", md: "md"}}/>
                    </FormControl>

                    <FormControl>
                        <FormLabel htmlFor="password" fontSize={{base: "sm", md: "md"}}>
                            Mot de passe :
                        </FormLabel>
                        <Input type="password" id="password" name="password" ref={passwordRef}
                               size={{base: "sm", md: "md"}}/>
                    </FormControl>

                    <FormControl>
                        <FormLabel htmlFor="name" fontSize={{base: "sm", md: "md"}}>
                            Prenom / Nom :
                        </FormLabel>
                        <Input type="text" id="name" name="name" ref={nameRef} size={{base: "sm", md: "md"}}/>
                    </FormControl>

                    <FormControl>
                        <FormLabel htmlFor="role" fontSize={{base: "sm", md: "md"}}>
                            Role :
                        </FormLabel>
                        <Select id="role" name="role" ref={roleRef} size={{base: "sm", md: "md"}}>
                            <option value="1">Developpeur</option>
                            <option value="2">Graphiste</option>
                            <option value="3">Community Manager</option>
                            <option value="4">Photographe</option>
                            <option value="5">Administratif</option>
                            <option value="6">Amenagement d'espace</option>
                            <option value="7">Up'Ink</option>
                            <option value="8">Administrateur</option>
                        </Select>
                    </FormControl>

                    <Button colorScheme={"blue"} variant={"outline"} size={{base: "sm", md: "md"}} mt={4} type="submit"
                            width={{base: "full", md: "auto"}}>
                        Ajouter un collaborateur
                    </Button>
                </Stack>

                <Header fontSize={{base: "lg", md: "xl"}}>Visibilite des collaborateurs</Header>

                <Stack spacing={3} width="full">
                    {users.map(user => (
                        <HStack
                            key={user.id}
                            justifyContent="space-between"
                            borderWidth="1px"
                            borderColor="gray.200"
                            borderRadius="md"
                            px={3}
                            py={2}
                        >
                            <Box minW={0}>
                                <Text fontWeight="bold" noOfLines={1}>{user.name}</Text>
                                <Text fontSize="sm" color="gray.600" noOfLines={1}>{user.email}</Text>
                            </Box>
                            <Switch
                                colorScheme="green"
                                isChecked={Boolean(user.is_active)}
                                onChange={() => handleToggleUser(user)}
                            />
                        </HStack>
                    ))}
                </Stack>
            </Stack>
        </Box>
    );
}
